/**
 * Countdown hook based on a previously recorded action timestamp.
 *
 * Use this when the cooldown source of truth comes from persisted data, such as
 * a backend field like `lastInvitationTime`. It returns the number of seconds
 * remaining until the action may be performed again, or `null` when no
 * cooldown is active.
 *
 * For client-only cooldowns that start immediately in the current session,
 * prefer `useCooldown`.
 */
import { useState, useEffect } from 'react';
import { differenceInSeconds } from 'date-fns';

type ReturnProps = [number | null];

const useCountdown = (
  lastActionTime: string | null | undefined,
  seconds: number,
): ReturnProps => {
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(
    getTimeLeft(lastActionTime, seconds),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const counterTime = getTimeLeft(lastActionTime, seconds);
      setRemainingSeconds(counterTime);

      if (counterTime != null && counterTime < 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => {
      if (timer != null) {
        clearInterval(timer);
      }
    };
  }, [lastActionTime, seconds]);

  return [remainingSeconds];
};

export const getTimeLeft = (
  lastActionTime: string | null | undefined,
  seconds: number,
): number | null => {
  if (lastActionTime == null) return null;
  const currentTime = new Date();
  const invitationTime = new Date(lastActionTime);
  const timeDifference = differenceInSeconds(currentTime, invitationTime);

  if (seconds - timeDifference <= 0) return null;
  return seconds - timeDifference;
};

export default useCountdown;
